import type { InternalAxiosRequestConfig } from 'axios';

// Every compute call is built by `onPath(serviceId, …)`, which writes the service UUID into
// the path because that is the shape @mgmt proxies. The compute controlplane serves the
// plain `/instances` and wants to know which subscribed service a request is about from the
// `X-Compute-Metadata-ID` header. This scope resolves the UUID in the path to the provider
// metadata id the service-detail endpoint returned, lifts it into the header and strips the
// prefix. It belongs to the compute client (a `createClient` option), not to the module.
const computeProxyPath = /^\/services\/([^/]+)\/S10003\/(.*)$/i;

interface ComputeScope {
  /** Remember (or, with no `metadataID`, forget) the metadata id of a service. */
  setMetadataID: (serviceID: string, metadataID?: string) => void;
  /** Request interceptor: pass it to `createClient(…, { requestInterceptors })`. */
  interceptor: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
}

export function createComputeScope(): ComputeScope {
  const metadataIDByServiceID = new Map<string, string>();
  return {
    setMetadataID(serviceID, metadataID) {
      if (metadataID) metadataIDByServiceID.set(serviceID, metadataID);
      else metadataIDByServiceID.delete(serviceID);
    },
    interceptor(config) {
      const match = computeProxyPath.exec(config.url ?? '');
      if (!match) return config;
      const [, serviceId, rest] = match;
      const metadataID = metadataIDByServiceID.get(serviceId);
      if (!metadataID) {
        return Promise.reject(new Error('Compute service metadata is not loaded'));
      }
      config.url = `/${rest}`;
      config.headers.set('X-Compute-Metadata-ID', metadataID);
      return config;
    },
  };
}
